import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, AlertCircle, Heart } from 'lucide-react';
import api from '../lib/api';

const POLL_MAX = 8;
const POLL_INTERVAL = 2000;

export default function SponsorshipSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState({ phase: 'polling', amount: 0, currency: 'eur' });

  useEffect(() => {
    if (!sessionId) { setStatus({ phase: 'error' }); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus({ phase: 'paid', amount: (data.amount_total || 0) / 100, currency: data.currency || 'eur' });
          return;
        }
        if (data.status === 'expired' || data.status === 'canceled') {
          setStatus({ phase: 'failed' });
          return;
        }
        if (attempts >= POLL_MAX) {
          setStatus({ phase: 'timeout' });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attempts >= POLL_MAX) { setStatus({ phase: 'error' }); return; }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main data-testid="sponsorship-success-page" className="min-h-[80vh] grid place-items-center pt-32 pb-20 bg-bone">
      <div className="container-x max-w-xl mx-auto text-center">
        {status.phase === 'paid' ? (
          <>
            <div className="w-20 h-20 grid place-items-center rounded-full bg-leaf/15 text-leaf mx-auto mb-6">
              <Heart size={40} />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight" data-testid="sponsorship-success">
              {t('sponsorship.success_title')}
            </h1>
            <p className="mt-4 text-ink-soft text-lg">{t('sponsorship.success_sub')}</p>
            {status.amount > 0 && (
              <p className="mt-2 font-heading text-2xl text-forest">{status.amount.toFixed(2)} {status.currency.toUpperCase()}</p>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/" className="btn-primary">{t('sponsorship.back_home')}</Link>
              <Link to="/sponsorship" className="btn-outline">{t('sponsorship.view_plans')}</Link>
            </div>
          </>
        ) : status.phase === 'failed' || status.phase === 'error' ? (
          <>
            <div className="w-20 h-20 grid place-items-center rounded-full bg-red-100 text-red-700 mx-auto mb-6">
              <AlertCircle size={40} />
            </div>
            <h1 className="font-heading text-4xl text-ink">{t('sponsorship.fail_title')}</h1>
            <p className="mt-4 text-ink-soft">{t('sponsorship.fail_sub')}</p>
            <Link to="/sponsorship" className="mt-6 inline-block btn-outline">{t('sponsorship.back_to_plans')}</Link>
          </>
        ) : status.phase === 'timeout' ? (
          <>
            <div className="w-20 h-20 grid place-items-center rounded-full bg-sun/20 text-ink mx-auto mb-6">
              <Clock size={40} />
            </div>
            <h1 className="font-heading text-3xl text-ink">{t('sponsorship.timeout_title')}</h1>
            <p className="mt-4 text-ink-soft">{t('sponsorship.timeout_sub')}</p>
            <Link to="/sponsorship" className="mt-6 inline-block btn-outline">{t('sponsorship.back_to_plans')}</Link>
          </>
        ) : (
          <>
            <div className="w-20 h-20 grid place-items-center rounded-full bg-sand text-ink-soft mx-auto mb-6 animate-pulse">
              <Clock size={40} />
            </div>
            <h1 className="font-heading text-3xl text-ink">{t('sponsorship.checking_title')}</h1>
            <p className="mt-4 text-ink-soft">{t('sponsorship.checking_sub')}</p>
          </>
        )}
      </div>
    </main>
  );
}
